import React from 'react';
import { isReleaseCategory } from '../../helpers';
import { ChangelogProps } from '../../types';
import { ChevronRightIcon } from '@heroicons/react/24/solid';

const PINK = 'rgb(190, 0, 128)';

const parseRichText = (text: string): React.ReactNode[] => {
  const regex = /(\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\))/g;
  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  text.replace(
    regex,
    (match: string, _: unknown, boldText: string, linkText: string, linkUrl: string, index: number) => {
      if (lastIndex < index) {
        result.push(text.slice(lastIndex, index));
      }

      if (boldText) {
        result.push(<strong key={index}>{boldText}</strong>);
      }

      if (linkText && linkUrl) {
        result.push(
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            key={index}
            className="changelog-link"
            style={{ color: PINK, textDecoration: 'underline' }}
          >
            {linkText}
          </a>,
        );
      }

      lastIndex = index + match.length;
      return '';
    },
  );

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
};

const customEntry = (entry: string): React.ReactNode[] => {
  return (<span>{parseRichText(entry)}</span>) as unknown as React.ReactNode[];
};

const contentStyle: React.CSSProperties = { color: 'inherit' };

const BulletItem: React.FC<{ children: React.ReactNode; isSubitem?: boolean }> = ({
  children,
  isSubitem = false,
}) => (
  <>
    {isSubitem ? (
      <li
        className="sub-bullet about-changelog-sub"
        style={{ listStyle: 'none', display: 'flex', alignItems: 'flex-start', marginBottom: '0.25rem' }}
      >
        <ChevronRightIcon
          style={{ width: '1rem', color: PINK, marginRight: '0.25rem', flexShrink: 0, marginLeft: '25px' }}
          aria-hidden
        />
        <span style={contentStyle}>{children}</span>
      </li>
    ) : (
      <li style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
        <span className="bullet-item" aria-hidden style={{ flexShrink: 0 }} />
        <span style={contentStyle}>{children}</span>
      </li>
    )}
  </>
);

type EntryType = 'p' | 'sub' | 'bullet';

function getEntryType(entry: string): EntryType {
  if (typeof entry !== 'string') return 'p';
  if (entry.startsWith('sub::')) return 'sub';
  if (entry.startsWith('p::')) return 'p';
  return 'bullet';
}

function getEntryRawText(entry: string, type: EntryType): string {
  if (type === 'p') return entry.slice(3);
  if (type === 'sub') return entry.slice(5);
  return entry;
}

const Changelog: React.FC<ChangelogProps> = ({ changelog }) => {
  const filteredChangelog = Object.entries(changelog).filter(
    ([category, entries]) =>
      category != null && String(category).trim() !== '' && Array.isArray(entries) && entries.length > 0,
  );

  return (
    <div className="changelog-content about-changelog" style={{ color: '#1f2937' }}>
      {filteredChangelog.map(([category, entries]) => {
        const listItems: React.ReactNode[] = [];
        let currentList: React.ReactNode[] = [];

        const flushList = () => {
          if (currentList.length > 0) {
            listItems.push(
              <ul
                key={listItems.length}
                className="about-changelog-list list-none pl-0 mb-2"
                style={{ listStyle: 'none', paddingLeft: 0 }}
              >
                {currentList}
              </ul>,
            );
            currentList = [];
          }
        };

        entries.forEach((entry, idx) => {
          const raw = entry as string;
          const type = getEntryType(raw);
          const rawText = getEntryRawText(raw, type);
          const parsed = customEntry(rawText);

          if (type === 'p') {
            flushList();
            listItems.push(
              <p key={`p-${idx}`} className="mb-2" style={{ marginBottom: '0.5rem' }}>
                {parsed}
              </p>,
            );
            return;
          }

          if (type === 'sub') {
            currentList.push(
              <BulletItem key={idx} isSubitem>
                {parsed}
              </BulletItem>,
            );
            return;
          }

          currentList.push(<BulletItem key={idx}>{parsed}</BulletItem>);
        });
        flushList();

        return (
          <div key={category} className="my-4">
            {isReleaseCategory(category) && <h2 className="version-number">{category.split('-')[1]}</h2>}
            {category && !category.includes('release') && <h6>{category}:</h6>}
            {listItems}
          </div>
        );
      })}
    </div>
  );
};

export default Changelog;
